import { useEffect, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import HistoryIcon from "@mui/icons-material/History";
import SwitchAccountOutlinedIcon from "@mui/icons-material/SwitchAccountOutlined";
import { format } from "date-fns";
import { AuthCard } from "~/components/AuthCard";
import { getDenial, googleRedirectUrl } from "~/services/auth";
import { navigateTo } from "~/utils/navigation";
import { COLORS } from "~/ui/tokens";
import { usePageTitle } from "~/shared/ui/usePageTitle";
import { DENIAL_VARIANTS, variantFor } from "./variants";

const TONES = {
  warning: { bg: "#fff3e0", fg: "#ef6c00" },
  error: { bg: "#fdecea", fg: "#d32f2f" },
  neutral: { bg: "#eceff1", fg: "#546e7a" },
};

// Lê só ?ref= (spec §2.11, P1-6). Motivo e e-mails vêm de GET auth/denial/:ref; nada da URL é exibido.
function useDenial(ref, enabled) {
  const [state, setState] = useState({ loading: Boolean(enabled && ref), denial: null });
  useEffect(() => {
    if (!enabled || !ref) {
      setState({ loading: false, denial: null });
      return undefined;
    }
    let alive = true;
    setState({ loading: true, denial: null });
    getDenial(ref).then(
      (denial) => alive && setState({ loading: false, denial }),
      () => alive && setState({ loading: false, denial: null }) // 404/429/rede → genérica
    );
    return () => {
      alive = false;
    };
  }, [ref, enabled]);
  return state;
}

export default function AccessDenied({ variant, embedded = false }) {
  usePageTitle("Acesso não liberado");
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref");
  const { loading, denial } = useDenial(ref, !variant);

  if (loading) {
    return (
      <AuthCard maxWidth={520} embedded={embedded}>
        <CircularProgress aria-label="Carregando" sx={{ color: COLORS.accent }} />
      </AuthCard>
    );
  }

  const key = variant || variantFor(denial?.reason);
  const v = DENIAL_VARIANTS[key];
  const tone = TONES[v.tone];
  const Icon = v.icon;

  return (
    <AuthCard maxWidth={520} embedded={embedded}>
      <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: tone.bg, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
        <Icon sx={{ color: tone.fg, fontSize: 32 }} aria-hidden />
      </Box>
      <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700, color: COLORS.primary, mb: 1.5 }}>{v.title}</Typography>
      <Typography data-testid="denial-body" sx={{ color: COLORS.text, mb: 1 }}>{v.body(denial)}</Typography>
      {v.hint && <Typography sx={{ color: COLORS.muted, mb: 2 }}>{v.hint}</Typography>}
      {v.info && <Alert severity="info" sx={{ textAlign: "left", my: 2 }}>{v.info}</Alert>}
      {denial?.attemptedAt && (
        <Typography variant="caption" component="p" sx={{ color: COLORS.muted, mb: 2, display: "flex", gap: 0.5, justifyContent: "center", alignItems: "center" }}>
          <HistoryIcon sx={{ fontSize: 15 }} aria-hidden />
          <span>{`Tentativa registrada em ${format(new Date(denial.attemptedAt), "dd/MM/yyyy HH:mm")}`}</span>
        </Typography>
      )}
      {v.action === "switch" && (
        <Button variant="contained" startIcon={<SwitchAccountOutlinedIcon />} onClick={() => navigateTo(googleRedirectUrl())} sx={{ bgcolor: COLORS.primary, textTransform: "none" }}>
          Entrar com outra conta
        </Button>
      )}
      {v.action === "retry" && (
        <Button variant="contained" onClick={() => navigateTo(googleRedirectUrl())} sx={{ bgcolor: COLORS.primary, textTransform: "none" }}>
          Tentar de novo
        </Button>
      )}
      {v.action === "home" && (
        <Button component={RouterLink} to="/admin" variant="contained" sx={{ bgcolor: COLORS.primary, textTransform: "none" }}>
          Ir para o início
        </Button>
      )}
    </AuthCard>
  );
}
